import { useState } from 'react';
import { PermissionGuard } from '@/features/auth';
import { useUserPermissionsQuery } from '../hooks/useUserPermissionsQuery';
import { useAvailablePermissionsQuery } from '../hooks/useAvailablePermissionsQuery';
import { useGrantPermissionMutation } from '../hooks/useGrantPermissionMutation';
import { useRevokePermissionMutation } from '../hooks/useRevokePermissionMutation';
import { Alert, Badge, Button, LoadingSpinner, Select } from '@/shared/components';
import { roleLabel } from '../utils/roleLabel';

interface Props {
  userId: string;
}

export function UserPermissionsPanel({ userId }: Props) {
  const [selectedPermId, setSelectedPermId] = useState('');

  const permissionsQuery = useUserPermissionsQuery(userId);
  const availablePermissionsQuery = useAvailablePermissionsQuery();
  const grantMutation = useGrantPermissionMutation();
  const revokeMutation = useRevokePermissionMutation();

  const permissions = permissionsQuery.data;
  const availablePerms = availablePermissionsQuery.data ?? [];

  // Mutation errors surface via the hook-level handleUserMutationError toast;
  // no inline alerts here to avoid duplicating the toast surface.
  const handleGrant = () => {
    if (!selectedPermId) return;
    grantMutation.mutate(
      { userId, permissionId: selectedPermId },
      { onSuccess: () => setSelectedPermId('') },
    );
  };

  const handleRevoke = (permissionId: string) => {
    revokeMutation.mutate({ userId, permissionId });
  };

  if (permissionsQuery.isLoading) {
    return <p>Loading permissions...</p>;
  }

  return (
    <div className="space-y-8 max-w-2xl">
      <div>
        <h3 className="font-semibold mb-2">Directly assigned Permissions</h3>
        {(!permissions?.direct || permissions.direct.length === 0) && (
          <p className="text-mp-muted text-sm">No direct permissions assigned.</p>
        )}
        <ul className="space-y-2">
          {permissions?.direct.map((perm, idx) => {
            const id = perm.id ?? `${perm.resource}.${perm.action}-${idx}`;
            return (
              <li key={id} className="flex items-center justify-between gap-2 p-2 border border-mp-border rounded">
                <div className="flex items-center gap-2">
                  <Badge variant="outline">{perm.resource}</Badge>
                  <span className="font-medium">{perm.action}</span>
                  {perm.name && (
                    <span className="text-mp-muted text-sm">{perm.name}</span>
                  )}
                </div>
                <PermissionGuard permission={{ resource: 'user', action: 'grant_direct_permission' }}>
                  {perm.id ? (
                    <Button
                      size="xs"
                      variant="danger"
                      onClick={() => {
                        if (!window.confirm(`Revoke permission '${perm.resource}.${perm.action}' from this user?`)) return;
                        handleRevoke(perm.id as string);
                      }}
                      disabled={revokeMutation.isPending}
                    >
                      Revoke
                    </Button>
                  ) : null}
                </PermissionGuard>
              </li>
            );
          })}
        </ul>
      </div>

      {permissions?.via_roles && permissions.via_roles.length > 0 && (
        <div>
          <h3 className="font-semibold mb-2">Permissions assigned via Roles</h3>
          <div className="space-y-4">
            {permissions.via_roles.map((entry) => (
              <div key={entry.role.id} className="border border-mp-border rounded p-3">
                <div className="flex items-center gap-2 mb-2">
                  <span className="font-medium">{roleLabel(entry.role)}</span>
                  {entry.role.is_elevated && (
                    <Badge variant="warning" size="xs">Elevated</Badge>
                  )}
                </div>
                {entry.permissions.length === 0 && (
                  <p className="text-mp-muted text-sm">No permissions in this role.</p>
                )}
                <ul className="flex flex-wrap gap-2">
                  {entry.permissions.map((perm, idx) => (
                    <li key={`${perm.resource}.${perm.action}-${idx}`}>
                      <Badge variant="info">
                        {perm.resource}.{perm.action}
                      </Badge>
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        </div>
      )}

      <div>
        <h3 className="font-semibold mb-2">Grant Permission</h3>
        {availablePermissionsQuery.isError && (
          <Alert variant="error">Failed to load available permissions.</Alert>
        )}
        <div className="flex gap-2 items-end flex-wrap">
          <div className="flex-1 min-w-[200px]">
            <Select
              value={selectedPermId}
              disabled={availablePermissionsQuery.isLoading}
              onChange={(value) => setSelectedPermId(value)}
              placeholder="Select a permission..."
              options={availablePerms.map((perm) => (
                { value: perm.id, label: `${perm.resource}.${perm.action}` }
              ))}
            />
          </div>
          {availablePermissionsQuery.isLoading && <LoadingSpinner size="sm" decorative className="ml-2" />}
          <PermissionGuard permission={{ resource: 'user', action: 'grant_direct_permission' }}>
            <Button
              size="sm"
              onClick={handleGrant}
              disabled={!selectedPermId || grantMutation.isPending}
            >
              Grant
            </Button>
          </PermissionGuard>
        </div>
      </div>
    </div >
  );
}
